import {
  ALL_RECIPIENT_UNITS,
  RECIPIENT_MAC_UNITS,
} from "../../../constants/investmentLeague";

/**
 * Normalizes unit name to check against standard 11 units.
 */
export const getCanonicalUnitName = (name) => {
  if (!name) return null;
  const trimmed = name.trim();
  const directMatch = ALL_RECIPIENT_UNITS.find(
    (u) => u.toLowerCase() === trimmed.toLowerCase()
  );
  if (directMatch) return directMatch;

  // Match prefix e.g. "MAC 1", "MAC 2"
  const macMatch = ALL_RECIPIENT_UNITS.find(
    (u) => trimmed.toLowerCase().startsWith(u.split(" - ")[0].toLowerCase())
  );
  if (macMatch) return macMatch;

  return null;
};

/**
 * Extracts compact short name for charts & badges (e.g. "MAC 1", "TSG").
 */
export const getUnitShortName = (unitName) => {
  if (!unitName) return "";
  if (unitName.includes(" - ")) {
    return unitName.split(" - ")[0].trim();
  }
  return unitName.trim();
};

/**
 * Determines unit type: "MAC" or "Group".
 */
export const getUnitType = (unitName, apiType) => {
  if (apiType === "MAC" || apiType === "Group") return apiType;
  if (unitName && unitName.toUpperCase().startsWith("MAC")) return "MAC";
  return "Group";
};

/**
 * Maps raw API response into sanitized 11 recipient units.
 */
export const mapRecipientLeaderboardResponse = (response) => {
  if (!response) {
    return {
      summary: null,
      units: [],
    };
  }

  const rawUnits = Array.isArray(response.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];

  // Filter and normalize to only the 11 valid recipient units (exclude TAC or any others)
  const validUnits = rawUnits
    .map((item) => {
      const canonicalName = getCanonicalUnitName(item.unitName || item.unit || item.name);
      if (!canonicalName) return null;

      const unitType = getUnitType(canonicalName, item.unitType);
      const shortName = getUnitShortName(canonicalName);

      const q1Investment = Number(item.q1Investment) || 0;
      const q1Return = Number(item.q1Return) || 0;
      const q1Roi = item.q1Roi != null ? Number(item.q1Roi) : null;

      const q2Investment = Number(item.q2Investment) || 0;
      const q2Return = Number(item.q2Return) || 0;
      const q2Roi = item.q2Roi != null ? Number(item.q2Roi) : null;

      const q3Investment = Number(item.q3Investment) || 0;
      const q3Return = Number(item.q3Return) || 0;
      const q3Roi = item.q3Roi != null ? Number(item.q3Roi) : null;

      const q4Investment = Number(item.q4Investment) || 0;
      const q4Return = Number(item.q4Return) || 0;
      const q4Roi = item.q4Roi != null ? Number(item.q4Roi) : null;

      const totalInvestment = Number(item.totalInvestment) || (q1Investment + q2Investment + q3Investment + q4Investment);
      const totalReturn = Number(item.totalReturn) || (q1Return + q2Return + q3Return + q4Return);
      const overallRoi = item.overallRoi != null
        ? Number(item.overallRoi)
        : totalInvestment > 0
        ? (totalReturn / totalInvestment) * 100
        : null;

      const topInvestors = Array.isArray(item.topInvestors)
        ? item.topInvestors.map((inv, idx) => ({
            rank: inv.rank || idx + 1,
            investorUnit: inv.investorUnit || inv.investor || inv.unit || "Unknown",
            amount: inv.investedAmount != null ? Number(inv.investedAmount) : Number(inv.amount) || 0,
            investedAmount: inv.investedAmount != null ? Number(inv.investedAmount) : Number(inv.amount) || 0,
            return: Number(inv.return) || Number(inv.amount) || 0,
            roi: inv.roi != null ? Number(inv.roi) : null,
          }))
        : [];

      return {
        rank: item.rank || 0,
        unitName: canonicalName,
        shortName,
        unitType,
        q1Investment,
        q1Return,
        q1Roi,
        q2Investment,
        q2Return,
        q2Roi,
        q3Investment,
        q3Return,
        q3Roi,
        q4Investment,
        q4Return,
        q4Roi,
        totalInvestment,
        totalReturn,
        overallRoi,
        topInvestors,
      };
    })
    .filter(Boolean);

  // If any of the 11 units was not returned by API, create default zeroed entry
  ALL_RECIPIENT_UNITS.forEach((unitName) => {
    const exists = validUnits.some((u) => u.unitName === unitName);
    if (!exists) {
      const unitType = RECIPIENT_MAC_UNITS.includes(unitName) ? "MAC" : "Group";
      validUnits.push({
        rank: validUnits.length + 1,
        unitName,
        shortName: getUnitShortName(unitName),
        unitType,
        q1Investment: 0,
        q1Return: 0,
        q1Roi: null,
        q2Investment: 0,
        q2Return: 0,
        q2Roi: null,
        q3Investment: 0,
        q3Return: 0,
        q3Roi: null,
        q4Investment: 0,
        q4Return: 0,
        q4Roi: null,
        totalInvestment: 0,
        totalReturn: 0,
        overallRoi: null,
        topInvestors: [],
      });
    }
  });

  return {
    rawSummary: response.summary || null,
    units: validUnits,
  };
};

/**
 * Computes filtered KPI cards based on selected Quarter and Unit Type.
 */
export const computeFilteredSummary = (units, selectedQuarter = "All Quarters", selectedUnitType = "All Units", rawSummary = null) => {
  let filtered = [...units];

  if (selectedUnitType === "MAC") {
    filtered = filtered.filter((u) => u.unitType === "MAC");
  } else if (selectedUnitType === "Groups") {
    filtered = filtered.filter((u) => u.unitType === "Group");
  }

  // If All Quarters and All Units and rawSummary exists, use rawSummary values if available
  if (selectedQuarter === "All Quarters" && selectedUnitType === "All Units" && rawSummary) {
    return {
      totalInvestment: rawSummary.totalInvestment ?? 0,
      totalReturn: rawSummary.totalReturn ?? 0,
      mostInvestedUnit: rawSummary.mostInvestedUnit || "-",
      mostInvestedAmount: rawSummary.mostInvestedAmount ?? 0,
      highestRoiUnit: rawSummary.highestRoiUnit || "-",
      highestRoi: rawSummary.highestRoi ?? 0,
      periodLabel: "vs previous period",
    };
  }

  const getQuarterInvestment = (u) => {
    switch (selectedQuarter) {
      case "Q1": return u.q1Investment;
      case "Q2": return u.q2Investment;
      case "Q3": return u.q3Investment;
      case "Q4": return u.q4Investment;
      default:   return u.totalInvestment;
    }
  };

  const getQuarterReturn = (u) => {
    switch (selectedQuarter) {
      case "Q1": return u.q1Return;
      case "Q2": return u.q2Return;
      case "Q3": return u.q3Return;
      case "Q4": return u.q4Return;
      default:   return u.totalReturn;
    }
  };

  const getQuarterRoi = (u) => {
    switch (selectedQuarter) {
      case "Q1": return u.q1Roi;
      case "Q2": return u.q2Roi;
      case "Q3": return u.q3Roi;
      case "Q4": return u.q4Roi;
      default:   return u.overallRoi;
    }
  };

  const totalInvestment = filtered.reduce((acc, u) => acc + getQuarterInvestment(u), 0);
  const totalReturn = filtered.reduce((acc, u) => acc + getQuarterReturn(u), 0);

  let mostInvestedUnit = "-";
  let mostInvestedAmount = 0;
  filtered.forEach((u) => {
    const inv = getQuarterInvestment(u);
    if (inv > mostInvestedAmount) {
      mostInvestedAmount = inv;
      mostInvestedUnit = u.unitName;
    }
  });

  let highestRoiUnit = "-";
  let highestRoi = null;
  filtered.forEach((u) => {
    const roi = getQuarterRoi(u);
    const inv = getQuarterInvestment(u);
    if (roi != null && inv > 0) {
      if (highestRoi === null || roi > highestRoi) {
        highestRoi = roi;
        highestRoiUnit = u.unitName;
      }
    }
  });

  return {
    totalInvestment,
    totalReturn,
    mostInvestedUnit,
    mostInvestedAmount,
    highestRoiUnit,
    highestRoi: highestRoi ?? 0,
    periodLabel: selectedQuarter !== "All Quarters" ? `${selectedQuarter} performance` : "vs previous period",
  };
};
